'use client';

import type { ECElementEvent } from 'echarts';
import type ReactEChartsCore from 'echarts-for-react/lib/core';
import { Chart } from '@/components/Chart';
import { StatsByRound } from '@/lib/query';
import { useEffect } from 'react';
import { useRef } from 'react';

export const VotesByRound = ({
  statsByRound,
  onSetRound,
  isMobile,
}: {
  statsByRound: StatsByRound;
  onSetRound: (round: number) => void;
  isMobile?: boolean;
}) => {
  const data = statsByRound.map((roundStats, idx) => ({
    name: isMobile ? `[${idx + 1}]` : `[${idx + 1}] ${roundStats.roundName}`,
    upvotes: roundStats.userUpvotes,
    downvotes: -roundStats.userDownvotes,
  }));

  const chartRef = useRef<ReactEChartsCore>(null);
  useEffect(() => {
    if (!chartRef.current) return;

    const chart = chartRef.current.getEchartsInstance();

    const handleClick = (params: ECElementEvent) => {
      const { componentType, dataIndex } = params;
      if (componentType === 'series') {
        onSetRound(dataIndex);
      }
    };

    chart.on('click', handleClick);

    return () => {
      chart.off('click', handleClick);
    }
  }, [onSetRound]);

  return (
    <Chart
      ref={chartRef}
      options={{
        responsive: true,
        maintainAspectRatio: false,
        grid: {
          top: '32px',
          bottom: '64px',
          left: '32px',
          right: '32px',
        },
        legend: {
          show: true,
          bottom: 0,
          textStyle: {
            color: '#a1a1aa',
          },
        },
        tooltip: {
          trigger: 'axis',
          axisPointer: {
            type: 'cross',
            crossStyle: {
              color: '#999'
            }
          }
        },
        xAxis: {
          type: 'category',
          data: data.map((d) => d.name),
          axisLabel: {
            hideOverlap: isMobile ? true : false,
            overflow: 'truncate',
            interval: 0,
            width: isMobile ? undefined : 760 / statsByRound.length,
            color: '#a1a1aa',
          },
          axisPointer: {
            type: 'shadow',
          },
          axisLine: {
            lineStyle: {
              color: '#4a4a4a',
            },
          },
        },
        yAxis: {
          type: 'value',
          axisPointer: {
            type: 'shadow',
          },
          splitNumber: 3,
          splitLine: {
            lineStyle: {
              color: '#27272a',
            },
          },
        },
        series: [
          {
            name: 'upvotes',
            type: 'bar',
            data: data.map((d) => d.upvotes),
            itemStyle: {
              color: '#2f9142',
            },
            emphasis: {
              focus: 'self'
            },
            barGap: '0%',
          },
          {
            name: 'downvotes',
            type: 'bar',
            data: data.map((d) => d.downvotes),
            itemStyle: {
              color: '#cc4e51',
            },
            emphasis: {
              focus: 'self'
            },
            barGap: '0%',
          },
        ],
      }}
    />
  )
};
